/**
 * Apple RoomPlan capture, as it arrives here.
 *
 * `CapturedRoom` and `CapturedStructure` are Swift value types; the field app
 * encodes them to JSON and uploads that. Every field below is typed the way JSON
 * actually arrives rather than the way Swift declares it — arrays of plain
 * numbers, optional keys, nullable parents — because the boundary between a
 * device and a server is where confident typing goes wrong. Nothing here assumes
 * a well-formed capture; `validateCapture` is the place that says so out loud.
 */

/**
 * RoomPlan's own three-level quality flag. It is qualitative: Apple publishes no
 * calibration for it, so see `confidence.ts` for how it becomes a number.
 */
import { splitCapturedRooms } from "./room-split.js";

export type RoomPlanConfidence = "low" | "medium" | "high";

/**
 * A parametric surface: wall, door, window, opening or floor.
 *
 * `dimensions` is `[x, y, z]` in metres where x is the width and y the height of
 * the surface in its own plane; z is its thickness and is never an area term.
 *
 * `transform` is a 4x4 column-major matrix. Columns 0, 1 and 2 are the surface's
 * local X, Y and Z axes in world space and column 3 is its centre, so the
 * translation is elements 12, 13, 14 and the outward normal is elements 8, 9, 10.
 */
export interface CapturedSurface {
  /** UUID string, stable within one capture. Used as the external reference. */
  readonly identifier: string;
  readonly dimensions: readonly number[];
  readonly transform: readonly number[];
  /** On windows, doors and openings: the identifier of the wall they sit in. */
  readonly parentIdentifier?: string | null;
  readonly confidence?: RoomPlanConfidence;
  /** Storey index assigned by RoomPlan during structure merging. */
  readonly story?: number;
  /** Present on non-rectangular surfaces; points are in the surface's local space. */
  readonly polygonCorners?: readonly (readonly number[])[];
  /** RoomPlan's own sub-classification, e.g. a door's "opening" vs "door". */
  readonly category?: string;
}

/**
 * A functional area RoomPlan believes it has recognised inside a room. The label
 * is the only room-type signal a capture carries, and it is a guess made by a
 * classifier looking at furniture.
 */
export interface CapturedSection {
  readonly identifier?: string;
  readonly label?: string;
  readonly story?: number;
}

/** Furniture and fittings. Not used for heat loss; carried so nothing is lost. */
export interface CapturedObject {
  readonly identifier: string;
  readonly category?: string;
  readonly dimensions?: readonly number[];
  readonly transform?: readonly number[];
  readonly confidence?: RoomPlanConfidence;
  readonly story?: number;
}

export interface CapturedRoom {
  readonly identifier?: string;
  readonly story?: number;
  readonly walls?: readonly CapturedSurface[];
  readonly doors?: readonly CapturedSurface[];
  readonly windows?: readonly CapturedSurface[];
  readonly openings?: readonly CapturedSurface[];
  readonly floors?: readonly CapturedSurface[];
  readonly objects?: readonly CapturedObject[];
  readonly sections?: readonly CapturedSection[];
  readonly version?: number;
}

/**
 * The result of merging several room captures. RoomStructure also republishes
 * merged, structure-wide `walls`/`doors`/… arrays, but those carry no room
 * membership and room membership is the whole point here, so `rooms` is the only
 * array read.
 */
export interface CapturedStructure {
  readonly identifier?: string;
  readonly rooms: readonly CapturedRoom[];
  readonly sections?: readonly CapturedSection[];
  readonly version?: number;
}

export type RoomPlanCapture = CapturedStructure | CapturedRoom;

/** Which array of a `CapturedRoom` a surface came out of. */
export type CapturedSurfaceKind = "wall" | "door" | "window" | "opening" | "floor";

export interface CapturedSurfaceRef {
  readonly kind: CapturedSurfaceKind;
  readonly surface: CapturedSurface;
}

/**
 * A single room capture and a merged structure are the same thing to everything
 * downstream, so the difference is flattened here once rather than at every use.
 *
 * And a whole home scanned in one go is told apart into its rooms here
 * (room-split.ts), once, so the plan, the heat-loss rooms, the calibrations,
 * the corrections and the exports all see the same rooms under the same
 * identities. A capture already scanned room by room comes back unchanged.
 */
export function capturedRooms(capture: RoomPlanCapture): readonly CapturedRoom[] {
  if ("rooms" in capture && Array.isArray(capture.rooms)) {
    return splitCapturedRooms(capture.rooms, capturedRoomRef);
  }
  return splitCapturedRooms(singleRoomList(capture as CapturedRoom), capturedRoomRef);
}

const SINGLE_ROOM_LISTS = new WeakMap<CapturedRoom, readonly CapturedRoom[]>();

/** One list per bare room, so the split below is computed once for it too. */
function singleRoomList(room: CapturedRoom): readonly CapturedRoom[] {
  const known = SINGLE_ROOM_LISTS.get(room);
  if (known) return known;
  const list = [room];
  SINGLE_ROOM_LISTS.set(room, list);
  return list;
}

/** Windows, doors and door-less openings — everything that punches a wall. */
export function roomOpenings(room: CapturedRoom): readonly CapturedSurfaceRef[] {
  return [
    ...(room.windows ?? []).map((surface) => ({ kind: "window" as const, surface })),
    ...(room.doors ?? []).map((surface) => ({ kind: "door" as const, surface })),
    ...(room.openings ?? []).map((surface) => ({ kind: "opening" as const, surface })),
  ];
}

/** Every parametric surface in the room, in a stable order. */
export function roomSurfaces(room: CapturedRoom): readonly CapturedSurfaceRef[] {
  return [
    ...(room.walls ?? []).map((surface) => ({ kind: "wall" as const, surface })),
    ...(room.floors ?? []).map((surface) => ({ kind: "floor" as const, surface })),
    ...roomOpenings(room),
  ];
}

/**
 * The identity a captured room goes by everywhere downstream: RoomPlan's own
 * room identifier when the capture carries one, otherwise one derived from its
 * surfaces (the lowest surface identifier), otherwise its position.
 *
 * `convertCapture` writes this as the survey room's `externalRef`, and
 * `scanRoomGroups` carries it on every `ScanRoomGroup` (and so every
 * `PlanRoom`), so a room drawn on the plan can be matched to the heat-loss
 * room it became — by this identity alone, never by name or position. One
 * function for both, so the two cannot drift apart.
 */
export function capturedRoomRef(room: CapturedRoom, index: number): string {
  if (typeof room.identifier === "string" && room.identifier.length > 0) {
    return room.identifier;
  }
  const ids = roomSurfaces(room)
    .map((s) => s.surface.identifier)
    .filter((id): id is string => typeof id === "string" && id.length > 0)
    .sort();
  return ids[0] !== undefined ? `room-${ids[0]}` : `room-${index}`;
}
